import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { contentService } from '../services/contentService';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../hooks/use-toast';
import {
  Users, Eye, Film, ExternalLink, UserPlus, UserCheck,
  Globe, Twitter, Instagram, Youtube
} from 'lucide-react';
import { cn } from '../lib/utils';
import { guildRolesUrl } from '../lib/guildUrl';
import { safeUrl } from '../lib/safeUrl';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const SOCIAL_ICONS = {
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube,
  website: Globe,
};

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
};

const StudioPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [publisher, setPublisher] = useState(null);
  useDocumentMeta({
    title: publisher?.name,
    description: publisher?.bio || undefined,
    image: publisher?.banner_url || publisher?.logo_url || undefined,
  });
  const [works, setWorks] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [openRoles, setOpenRoles] = useState([]);

  useEffect(() => {
    if (slug) {
      loadPublisher();
    }
  }, [slug]);

  useEffect(() => {
    if (publisher && user) {
      checkFollowStatus();
    }
  }, [publisher, user]);

  useEffect(() => {
    if (publisher) {
      loadWorks();
    }
  }, [publisher, sortBy]);

  const loadPublisher = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await contentService.getPublisherBySlug(slug);

      if (!data) {
        setError('Studio not found');
        return;
      }

      // In production, enforce brand_page_published. In dev, allow access.
      if (!data.brandPagePublished && data.status === 'approved') {
        // Brand page not published but publisher exists — show anyway in dev
        console.warn('Studio brand page not published yet, showing dev fallback');
      }

      setPublisher(data);
      setFollowerCount(data.followerCount);

      const { data: roles } = await supabase.rpc('public_studio_roles', {
        p_publisher_id: data.id,
      });
      setOpenRoles(roles || []);
    } catch (err) {
      console.error('Error loading publisher:', err);
      setError('Failed to load studio');
    } finally {
      setIsLoading(false);
    }
  };

  const loadWorks = async () => {
    if (!publisher) return;
    const data = await contentService.getPublisherWorks(publisher.id, { sortBy });
    setWorks(data);
  };

  const checkFollowStatus = async () => {
    if (!user || !publisher) return;
    try {
      const { data } = await supabase
        .from('publisher_followers')
        .select('id')
        .eq('user_id', user.id)
        .eq('publisher_id', publisher.id)
        .maybeSingle();
      setIsFollowing(!!data);
    } catch {
      setIsFollowing(false);
    }
  };

  const toggleFollow = useCallback(async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!publisher) return;

    try {
      if (isFollowing) {
        await supabase
          .from('publisher_followers')
          .delete()
          .eq('user_id', user.id)
          .eq('publisher_id', publisher.id);

        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        toast({ title: 'Unfollowed studio' });
      } else {
        await supabase
          .from('publisher_followers')
          .insert({ user_id: user.id, publisher_id: publisher.id });

        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        toast({ title: 'Following studio' });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      toast({ title: 'Failed to update follow', variant: 'destructive' });
    }
  }, [user, publisher, isFollowing, navigate, toast]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Skeleton className="w-full h-[40vh]" />
        <div className="container mx-auto px-4 -mt-16 relative z-10 space-y-6">
          <div className="flex items-end gap-6">
            <Skeleton className="w-24 h-24 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-24 w-full" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48" />)}
          </div>
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !publisher) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-6 text-center space-y-3">
            <h2 className="text-2xl font-bold">Studio not found</h2>
            <p className="text-muted-foreground">
              {error || "This studio doesn't exist or hasn't published their page yet."}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={() => window.history.back()}>Go Back</Button>
              <Button onClick={() => navigate('/browse')}>Browse Content</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const socialEntries = Object.entries(publisher.socialLinks || {}).filter(([, url]) => url);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Banner */}
      <div className="relative w-full" style={{ minHeight: '40vh' }}>
        {publisher.bannerUrl ? (
          <img
            src={publisher.bannerUrl}
            alt={`${publisher.companyName} banner`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-accent/20 to-primary/10" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      {/* Publisher Info */}
      <div className="container mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 mb-8">
          {/* Logo */}
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-card border-4 border-background shadow-xl overflow-hidden flex-shrink-0">
            {publisher.logoUrl ? (
              <img
                src={publisher.logoUrl}
                alt={publisher.companyName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary text-3xl font-bold">
                {publisher.companyName?.[0] || 'S'}
              </div>
            )}
          </div>

          {/* Name & Stats */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl md:text-4xl font-bold">{publisher.companyName}</h1>
              {publisher.isVerified && (
                <Badge className="bg-primary text-primary-foreground">Verified</Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {formatNumber(followerCount)} followers
              </span>
              <span className="flex items-center gap-1">
                <Film className="h-4 w-4" />
                {works.length} titles
              </span>
              {publisher.totalViews > 0 && (
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {formatNumber(publisher.totalViews)} views
                </span>
              )}
            </div>
          </div>

          {/* Follow Button */}
          <Button
            onClick={toggleFollow}
            variant={isFollowing ? 'outline' : 'default'}
            size="lg"
            className="rounded-full px-8"
          >
            {isFollowing ? (
              <>
                <UserCheck className="w-5 h-5 mr-2" />
                Following
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5 mr-2" />
                Follow
              </>
            )}
          </Button>
        </div>

        {/* Description */}
        {publisher.description && (
          <p className="text-lg text-muted-foreground max-w-3xl mb-6">
            {publisher.description}
          </p>
        )}

        {/* Social Links */}
        {socialEntries.length > 0 && (
          <div className="flex items-center gap-3 mb-8">
            {socialEntries.map(([platform, url]) => {
              const Icon = SOCIAL_ICONS[platform] || ExternalLink;
              return (
                <a
                  key={platform}
                  href={safeUrl(url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border hover:bg-accent transition-colors text-sm"
                >
                  <Icon className="h-4 w-4" />
                  <span className="capitalize">{platform}</span>
                </a>
              );
            })}
          </div>
        )}

        {openRoles.length > 0 && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardContent className="pt-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold">We&apos;re hiring</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Open crew roles on Kinora Guild — apply to join this studio&apos;s productions.
                </p>
              </div>
              <ul className="space-y-3">
                {openRoles.slice(0, 6).map((role) => (
                  <li key={role.role_id} className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/40 pb-2 last:border-0 last:pb-0">
                    <div>
                      <span className="font-medium">{role.role_title}</span>
                      <span className="text-muted-foreground"> · {role.project_title}</span>
                    </div>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {role.compensation?.replace('_', ' ')}
                      {role.rate_detail ? ` · ${role.rate_detail}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              <Button asChild variant="default">
                <a href={guildRolesUrl()} target="_blank" rel="noreferrer">
                  View roles on Kinora Guild
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* About Section */}
        {publisher.aboutUs && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-3">About</h2>
              <p className="text-muted-foreground whitespace-pre-line">{publisher.aboutUs}</p>
            </CardContent>
          </Card>
        )}

        {/* Content Library */}
        <section className="pb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Content Library</h2>
            <div className="flex gap-2">
              <Button
                variant={sortBy === 'newest' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('newest')}
              >
                Newest
              </Button>
              <Button
                variant={sortBy === 'popular' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('popular')}
              >
                Popular
              </Button>
            </div>
          </div>

          {works.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {works.map((work) => (
                <VideoCard key={work.id} video={work} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Film className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-1">No content yet</h3>
                <p className="text-muted-foreground">
                  This studio hasn't published any content yet. Check back soon!
                </p>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};

export default StudioPage;
